import getRegl from 'regl';
import { setC2 } from '@thi.ng/vectors/setc';
import { mulN2 } from '@thi.ng/vectors/muln';
import { divN2 } from '@thi.ng/vectors/divn';
import { invert2 } from '@thi.ng/vectors/invert';
import { hsl, srgb } from '@thi.ng/color';
import getFPS from 'fps-indicator';
import { map } from '@epok.tech/fn-lists/map';
import { reduce } from '@epok.tech/fn-lists/reduce';
import { each } from '@epok.tech/fn-lists/each';
import { wrap } from '@epok.tech/fn-lists/wrap';
import { range } from '@epok.tech/fn-lists/range';

import gpgpu from '@epok.tech/gl-gpgpu/src';
import { mapStep } from '@epok.tech/gl-gpgpu/src/maps';
import { getPass } from '@epok.tech/gl-gpgpu/src/step';

import {
    vertDef as screenVert,
    positionsDef,
    countDef as screenCount,
    preDef as gpgpuPre
  } from '@epok.tech/gl-gpgpu/src/const';

import {
    mpm, extensions, extensionsOptional,
    checkStatus, toLink, linkPast, linkFull, toFields
  } from '../src';

import gridFadeFrag from './grid-fade/index.frag.glsl';
import gridFadeToScreenFrag from './grid-fade-to-screen/index.frag.glsl';

import opticalFlowFrag from './optical-flow/index.frag.glsl';
import opticalFlowToGridFrag from './optical-flow-to-grid/index.frag.glsl';

const { min, max, sign, log2 } = Math;
const { isFinite, isInteger } = Number;
const { assign } = Object;
const { parse } = JSON;
const { createObjectURL } = URL;
const _ = undefined;

const screenPositions = positionsDef();

/**
 * @see [glsl-aspect](https://github.com/keeffEoghan/glsl-aspect/blob/master/src/index.glsl)
 * @see [glsl-aspect/contain](https://github.com/keeffEoghan/glsl-aspect/blob/master/src/contain.glsl)
 * @see [glsl-aspect/cover](https://github.com/keeffEoghan/glsl-aspect/blob/master/src/cover.glsl)
 */
export const aspect = (size, fit, invert, to = []) => {
  const [x, y] = size;
  const side = fit(x, y);

  ((side)? divN2(to, size, side) : setC2(to, 1, 1));

  return ((invert)? invert2(to, to) : to);
};

export function queryDef(to = location.search) {
  const q = new URLSearchParams(to);

  return (to = q.get('query'))? queryDef(to) : q;
}

export const queryAt = (at, alt, q = queryDef()) => q.get(at) || alt;
export const queryNumber = (at, alt, q) => parseFloat(queryAt(at, alt, q), 10);

export function queryFlagish(at, alt, q) {
  const s = queryAt(at, alt, q);

  return (s === 'true') || ((s !== 'false') && alt);
}

export const queryNumbers = (l, at, alt, to = range(l), q) =>
  reduce((to, _, i) => {
      const n = queryNumber(`${at}[${i}]`, alt?.[i], q);

      ((isFinite(n))? (to[i] = n) : (to[i] === undefined) && (delete to[i]));

      return to;
    },
    to, to);

export const queryExponent = (at, alt, q) =>
  map((v) => parseFloat(v, 10),
    parseFloat(queryAt(at, alt, q), 10).toExponential().split('e'), 0);

export function queryColor(at, alt, parse = srgb, to, q) {
  const s = queryAt(at, alt, q);
  const c = parse((((s[0] !== '#') && (s.indexOf('(') < 0))? '#' : '')+s).buf;

  return ((to)? assign(to, c) : c);
}

export const queryState = (q = queryDef(), at = '') => ({
  pixelRatio: queryNumber(at+'pixelRatio', max(devicePixelRatio, 1) || 1, q),
  timer: { step: queryNumber(at+'timer.step', 1e3/30, q) },
  g: queryNumbers(3, at+'g', _, _, q),
  pointsToGrid: {
    on: queryFlagish(at+'pointsToGrid.on', true, q),
    quads: queryFlagish(at+'pointsToGrid.quads', _, q),
    instanced: queryFlagish(at+'pointsToGrid.instanced', _, q),
    batch: queryFlagish(at+'pointsToGrid.batch', _, q),
    order: queryAt(at+'pointsToGrid.order', _, q)
  },
  grid: {
    on: queryFlagish(at+'grid.on', true, q),
    detail: queryNumber(at+'grid.detail', 90, q),
    limit: queryNumber(at+'grid.limit', 1e9, q),
    bounds: queryNumbers(4, at+'grid.bounds', [-1, -1, 1, 1], _, q),
    boundsIn: queryNumbers(4, at+'grid.boundsIn', [1, 1, -1, -1], _, q),
    boundsHit: queryNumber(at+'grid.boundsHit', 1, q)
  },
  gridToPoints: {
    on: queryFlagish(at+'gridToPoints.on', true, q),
    gpgpu: {
      scale: queryNumber(at+'gridToPoints.gpgpu.scale', 9, q),
      steps: queryNumber(at+'gridToPoints.gpgpu.steps', 2, q)
    },
    mass: queryNumber(at+'gridToPoints.mass', 1, q),
    volume: queryNumber(at+'gridToPoints.volume', 5e-4, q),
    volumeSplit:
      queryNumbers(2, at+'gridToPoints.volumeSplit', [1e-6, -1e-3], _, q),
    motionAdjust: queryNumber(at+'gridToPoints.motionAdjust', 0.97, q),
    positionAdjusts:
      queryNumbers(2, at+'gridToPoints.positionAdjusts', [0, 1], _, q),
    wander:
      queryNumbers(3, at+'gridToPoints.wander', [-1e-5, 1e-2, 1e-6], _, q),
    spawnOn: {
      any: queryFlagish(at+'gridToPoints.spawnOn.any', true, q),
      out: queryFlagish(at+'gridToPoints.spawnOn.out', true, q),
      slow: queryFlagish(at+'gridToPoints.spawnOn.slow', false, q),
      centre: queryNumber(at+'gridToPoints.spawnOn.centre', 0, q)
    },
    speedClamp: queryNumber(at+'gridToPoints.speedClamp', 1e9, _, q),
    boundsClamp: queryFlagish(at+'gridToPoints.boundsClamp', true, q)
  },
  gridToScreen: {
    on: queryFlagish(at+'gridToScreen.on', true, q),
    colorHi: queryColor(at+'gridToScreen.colorHi', '#000b', _, _, q),
    colorLo: queryColor(at+'gridToScreen.colorLo', '#0000', _, _, q),

    angleHSLA: queryColor(at+'gridToScreen.angleHSLA',
      'hsla(0deg 50% 50% / 0.2)', hsl, _, q)
  },
  pointsToScreen: {
    on: queryFlagish(at+'pointsToScreen.on', true, q),
    colorHi: queryColor(at+'pointsToScreen.colorHi', '#f847', _, _, q),
    colorLo: queryColor(at+'pointsToScreen.colorLo', '#0000', _, _, q),
    form: queryNumber(at+'pointsToScreen.form', 1, q)
  },
  demo: {
    fps: queryFlagish(at+'demo.fps', _, q),
    opticalFlow: {
      on: queryFlagish(at+'demo.opticalFlow.on', true, q),
      media: (() => {
        const to = {};
        const vq = at+'demo.opticalFlow.media.video';
        const v = queryAt(vq, _, q);
        const y = (v === 'true');

        if(y || (v === 'false')) {
          to.video = y;

          return to;
        }

        let size;
        const vw = queryNumber(vq+'.width', _, q);
        const vh = queryNumber(vq+'.height', _, q);

        isInteger(vw) && ((size ??= to.video = {}).width = vw);
        isInteger(vh) && ((size ??= to.video = {}).height = vh);

        return to;
      })(),
      $root: (() => {
        const r = queryFlagish(at+'demo.opticalFlow.$root', _, q);

        return ((r === true)? document.body : r && document.querySelector(r));
      })(),
      flip: queryNumbers(2, at+'demo.opticalFlow.flip', [-1, -1], _, q),
      offset:
        queryNumbers(4, at+'demo.opticalFlow.offset', [0, 0, 1, 1], _, q)
    },
    opticalFlowToGrid: {
      rescaleFlow:
        queryNumbers(2, at+'demo.opticalFlowToGrid.rescaleFlow', [2, 5], _, q)
    },
    gridFade: {
      on: queryFlagish(at+'demo.gridFade.on', true, q),
      fade: queryNumber(at+'demo.gridFade.fade', 2e-3, q)
    }
  },
  passes: queryNumber(at+'passes', 2, q)
});

export async function demo(to) {
  // Set up GL.
  const { $canvas, pixelRatio = devicePixelRatio } = to;

  const api = to.api = getRegl({
    canvas: $canvas, pixelRatio,
    extensions: extensions(), optionalExtensions: extensionsOptional(),
    ...to.api
  });

  const { command = api, frame, texture, buffer, framebuffer, clear } = api;

  checkStatus(to, () => mpm(api, to));

  const { grid, gridToScreen, pointsToScreen } = to;
  const { fields: { index: gIndex }, gpgpu: gGPGPU } = grid;
  const { pre: gPre, size: gSize, merge: gMerge, maps: gMaps } = gGPGPU;

  const demo = to.demo = { to, ...to.demo };

  /** Optical-flow interaction process. */

  const ofPre = 'opticalFlow_';
  const opticalFlowVert = screenVert.replaceAll(gpgpuPre, ofPre);
  const ofFlipStyle = [1, -1];

  const opticalFlow = demo.opticalFlow = {
    to,
    vert: opticalFlowVert,
    frag: opticalFlowFrag,
    count: screenCount,
    gap: 3, lambda: 1e-3, alpha: 1e2,
    flip: [-1, -1], aspect: [1, 1], size: [0, 0], offset: [0, 0, 1, 1],
    at: 0,
    $root: null,

    on: true,

    ...demo.opticalFlow,

    media: { facingMode: 'user', video: true, ...demo.opticalFlow?.media },
    input: {
      data: document.createElement('video'),
      min: 'linear', mag: 'linear',
      ...demo.opticalFlow?.input
    },
    frames: map(() => texture(), range(2), 0),
    /** Overwrite blend, don't look up past values, just set new values. */
    blend: { enable: true, func: { src: 1, dst: 0 } },
    depth: { enable: false },

    framebuffer:
      framebuffer({ colorType: 'float', depth: false, stencil: false })
  };

  opticalFlow.pass = command(opticalFlow.pipeline = {
    vert: (c, p) => p.vert,
    frag: (c, p) => p.frag,

    attributes: { opticalFlow_position: buffer(screenPositions) },

    uniforms: {
      next: (c, { frames: fs, at }) => wrap(at, fs),
      past: (c, { frames: fs, at }) => wrap(at+1, fs),
      gap: (c, p) => p.gap,
      lambda: (c, p) => p.lambda,
      alpha: (c, p) => p.alpha,
      flip: (c, p) => p.flip,
      offset: (c, p) => p.offset,
      frameAspect: (c, p) => p.aspect,
      screenAspect: (c, p) => p.to.screen.aspect
    },

    blend: {
      enable: (c, p) => p.blend.enable,

      func: {
        srcRGB: (c, { blend: { func: { src: o, srcRGB: s } } }) => s ?? o,
        srcAlpha: (c, { blend: { func: { src: o, srcAlpha: s } } }) => s ?? o,
        dstRGB: (c, { blend: { func: { dst: o, dstRGB: s } } }) => s ?? o,
        dstAlpha: (c, { blend: { func: { dst: o, dstAlpha: s } } }) => s ?? o
      }
    },

    depth: { enable: (c, p) => p.depth.enable },
    count: (c, p) => p.count,
    framebuffer: (c, p) => p.framebuffer
  });

  /** @todo Handle `merge.update` if `grid` has `merge`. */
  opticalFlow.step = (state = opticalFlow) => {
    const s = state.onStep?.(state) ?? state;
    const { on, pass, at, frames: fs, input: i } = s;

    if(!on) { return s; }

    fs[s.at = (at+1)%fs.length].subimage(i.data);
    pass(s);

    return s;
  };

  const ofgPre = 'opticalFlowToGrid_';
  const opticalFlowToGridVert = screenVert.replaceAll(gpgpuPre, ofgPre);

  const opticalFlowToGrid = demo.opticalFlowToGrid = {
    to,
    vert: opticalFlowToGridVert,
    frag: opticalFlowToGridFrag,
    count: screenCount,
    rescaleFlow: [2, 5],
    /** Additive blend, don't look up past values, just add any new values. */
    blend: { enable: true, func: { src: 1, dst: 1 } },
    depth: { enable: false },
    onStep: null,
    ...demo.opticalFlowToGrid
  };

  const ofgLinkG = toLink({
      ...linkPast,
      pre: gPre, steps: gSize.steps, merge: gMerge,
      macros: { samples: 0, taps: 0 },
      maps: mapStep({ ...gMaps, ...linkPast.maps, derives: null })
    },
    (c, p) => p.to.grid.gpgpu);

  opticalFlowToGrid.link = { grid: ofgLinkG };

  opticalFlowToGrid.pass = command(opticalFlowToGrid.pipeline = {
    vert: (c, p) => p.vert,

    frag: (c, { frag: f, link: l, to: { head: h } }) =>
      h.glsl(h)+l.grid.macros+f,

    attributes: { opticalFlowToGrid_position: buffer(screenPositions) },

    uniforms: {
      ...ofgLinkG.state.uniforms,
      rescaleFlow: (c, p) => p.rescaleFlow,
      opticalFlow: (c, p) => p.to.demo.opticalFlow.framebuffer.color[0],
      dt: (c, p) => p.to.timer.dt,
      rescaleMotion: (c, p) => p.to.rescale.motion
    },

    blend: {
      enable: (c, p) => p.blend.enable,

      func: {
        srcRGB: (c, { blend: { func: { src: o, srcRGB: s } } }) => s ?? o,
        srcAlpha: (c, { blend: { func: { src: o, srcAlpha: s } } }) => s ?? o,
        dstRGB: (c, { blend: { func: { dst: o, dstRGB: s } } }) => s ?? o,
        dstAlpha: (c, { blend: { func: { dst: o, dstAlpha: s } } }) => s ?? o
      }
    },

    depth: { enable: (c, p) => p.depth.enable },
    count: (c, p) => p.count,
    framebuffer: (c, p) => getPass(p.to.grid.gpgpu)?.framebuffer
  });

  opticalFlowToGrid.step = (state = opticalFlowToGrid) => {
    const s = state.onStep?.(state) ?? state;
    const { pass, to: { demo, grid } } = s;

    if(!demo.opticalFlow.on) { return s; }

    const { gpgpu: gGPGPU, fields: gf } = grid;
    const { maps: gm, passNow: gp, stepBy: gStepBy } = gGPGPU;

    gStepBy(gGPGPU, -1);
    gGPGPU.passNow = gm.valueToPass[gf.index.motionFull];
    pass(s);
    gGPGPU.merge?.update?.(gGPGPU);
    gGPGPU.passNow = gp;
    gStepBy(gGPGPU, 1);
  };

  /** Set up the renders when the input video is ready. */
  const ofSetup = opticalFlow.setup = async () => {
    const { demo: { opticalFlow, opticalFlowToGrid }, gridToPoints } = to;

    const { $root, input, frames, setup, offset, flip, aspect: ar, size } =
      opticalFlow;

    const gpGPGPU = gridToPoints.gpgpu;
    const { onStep: gpOnStep } = gpGPGPU;
    const $v = input.data;
    const style = $root?.appendChild?.($v)?.style;
    const { videoWidth: w, videoHeight: h } = $v;

    await $v.play();
    /** Aspect ratio, `GLSL`; use `min` for contain, `max` for cover. */
    aspect(setC2(size, w, h), max, true, ar);
    opticalFlow.framebuffer.resize(w, h);
    // Pixels units; divide `gap` by the input resolution.
    opticalFlow.gap /= max(w, h);
    // Fill the flow frames with the first frame.
    each((f) => f(input), frames);

    if(style) {
      style.transform = `scale(${
        reduce((s, f, i) => s+((s)? ',' : '')+(f*ofFlipStyle[i]), flip, '')})`;

      style.objectFit = 'cover';
      style.aspectRatio = w/h;
    }

    gpGPGPU.onStep = (gpGPGPU) => {
      const s = gpOnStep?.(gpGPGPU) ?? gpGPGPU;
      const { passAt, demo: { opticalFlow, opticalFlowToGrid } } = s.to;

      !passAt && opticalFlow.step(opticalFlow);
      opticalFlowToGrid.step(opticalFlowToGrid);

      return s;
    };

    opticalFlow.on = true;
    $v.removeEventListener('canplay', setup);
  };

  const ofStart = opticalFlow.start = async (state = opticalFlow) => {
    const { media: mq, setup, input: { data: $v } } = state;

    if(!mq.video) { return state; }

    try {
      const src = state.stream = await navigator.mediaDevices.getUserMedia(mq);

      $v.addEventListener('canplay', setup);
      (('srcObject' in $v)? $v.srcObject = src : $v.src = createObjectURL(src));
    }
    catch(e) { console.warn(e); }

    return state;
  };

  opticalFlow.stop = (state = opticalFlow) => {
    const { input: { data: $video }, $root } = state;
    const tracks = $video.srcObject?.getTracks?.();

    each((t) => t.stop(), tracks);
    $root?.removeChild?.($video);
    state.on = false;

    return state;
  };

  /** Grid fade. */
  const gridFade = demo.gridFade = {
    to,
    frag: gridFadeFrag,
    fade: 2e-3,
    expose: 0,
    scale: 3e1,
    order: 'next',
    on: true,
    ...demo.gridFade
  };

  const gfPre = 'gridFade_';
  const gfSteps = 2;
  const gfMerge = false;
  const gfFields = gridFade.fields = toFields((new Map()).set('fade', 4));

  const gfDerives = gridFade.derives = [];
  const { index: gfIndex, values: gfValues, alias: gfAlias } = gfFields;
  const { fade: gfFade } = gfIndex;

  gfDerives[gfFade] = [gfFade];

  const gfMaps = { values: gfValues, alias: gfAlias, derives: gfDerives };

  const gfGPGPU = gridFade.gpgpu = gpgpu(api, {
    to,
    pre: gfPre,
    steps: gfSteps, stepMax: gfSteps,
    stepNow: 0, passNow: 0,
    shape: [...gSize.shape],
    maps: gfMaps,
    merge: gfMerge,
    macros: { vert: 0 },

    frag: (c, { to: { demo: { gridFade: { frag: f, type, quality } } } }) =>
      ((type)? `#define type ${type}\n\n` : '')+
      ((quality)? `#define quality ${quality}\n\n` : '')+
      f,

    type: 'uint8',
    min: 'linear', mag: 'linear',

    uniforms: {
      fade: (c, p) => p.to.demo.gridFade.fade,
      expose: (c, p) => p.to.demo.gridFade.expose,
      scale: (c, p) => p.to.demo.gridFade.scale,

      colorLo: (c, p) => p.to.gridToScreen.colorLo,
      dt: (c, p) => p.to.timer.dt,
      chaos: (c, p) => p.to.chaos
    }
  });

  gridFade.step = (state = gridFade) => {
    const { on, type, gpgpu } = state;

    if(!on) { return state; }

    const { step } = gpgpu;

    step(gpgpu);
    ((type === 1) || (type === 'typeGauss')) && step(gpgpu);

    return state;
  };

  /** Grid fade to screen. */
  const gridFadeToScreen = demo.gridFadeToScreen = {
    to,
    frag: gridFadeToScreenFrag,

    blend: { enable: true, func: { src: 1, dst: 'one minus src alpha' } },
    depth: { enable: false }
  };

  const gfsLinkGF = toLink({
      ...linkFull, pre: gfPre, steps: gfSteps, merge: gfMerge,
      macros: { output: 0 },
      maps: mapStep({ ...gfMaps, ...linkFull.maps, derives: [[gfFade]] })
    },
    (c, p) => p.to.demo.gridFade.gpgpu);

  gridFadeToScreen.link = { gridFade: gfsLinkGF };

  gridFadeToScreen.pass = command(gridFadeToScreen.pipeline = {
    vert: (c, p) => p.to.demo.gridFade.gpgpu.vert,

    frag: (c, { frag, link: { gridFade: lgf }, to: { head: h } }) =>
      h.glsl(h)+lgf.macros+frag,

    attributes: { gridFade_position: buffer(screenPositions) },
    uniforms: { ...gfsLinkGF.state.uniforms },

    blend: {
      enable: (c, p) => p.blend.enable,

      func: {
        srcRGB: (c, { blend: { func: { src: o, srcRGB: s } } }) => s ?? o,
        srcAlpha: (c, { blend: { func: { src: o, srcAlpha: s } } }) => s ?? o,
        dstRGB: (c, { blend: { func: { dst: o, dstRGB: s } } }) => s ?? o,
        dstAlpha: (c, { blend: { func: { dst: o, dstAlpha: s } } }) => s ?? o
      }
    },

    depth: { enable: (c, p) => p.depth.enable },
    count: (c, p) => p.to.demo.gridFade.gpgpu.count
  });

  gridFadeToScreen.draw = (state = gridFadeToScreen) => {
    const { pass, to } = state;

    to.demo.gridFade.on && pass(state);

    return state;
  };

  const { onDraw: gsOnDraw } = gridToScreen;
  const { onDraw: psOnDraw } = pointsToScreen;

  gridToScreen.onDraw = (gridToScreen) => {
    const s = gsOnDraw?.(gridToScreen) ?? gridToScreen;
    const { gridFade } = s.to.demo;
    const { on, order, step, gpgpu } = gridFade;

    (order === 'past') && step(gridFade);
    gridToScreen.framebuffer = ((on)? getPass(gpgpu)?.framebuffer : null);

    return s;
  };

  pointsToScreen.onDraw = (pointsToScreen) => {
    const s = psOnDraw?.(pointsToScreen) ?? pointsToScreen;
    const { gridFade, gridFadeToScreen } = s.to.demo;
    const { order, step } = gridFade;

    (order === 'next') && step(gridFade);
    gridFadeToScreen.draw(gridFadeToScreen);

    return s;
  };

  demo.view ??= [];

  /** Resize the canvas and any dependent properties. */
  const resize = demo.resize = (s = demo) => {
    const demo = s.onResize?.(s) ?? s;
    const { to, gridFade, view, onResized } = demo;
    const { $canvas, screen, grid } = to;
    const [vw = innerWidth, vh = innerHeight] = view;
    const { bounds, limit, detail, gpgpu: gGPGPU } = grid;
    const gShape = gGPGPU.size.shape;
    const { size: ss, aspect: ar } = screen;
    const [w, h] = mulN2(ss, setC2(ss, vw, vh), pixelRatio);

    $canvas.width = w;
    $canvas.height = h;

    /** Aspect ratio, `JS`; use `min` for contain, `max` for cover. */
    aspect(ss, max, false, ar);
    /** Grid bounds resize proportional to screen. */
    map((b, i) => (limit*sign(b))*wrap(i, ar), bounds, 0);
    /** Grid data resize proportional to screen. */
    gGPGPU.resize(mulN2(gShape, ar, detail));
    /** Grid fade resize matches screen. */
    gridFade.gpgpu.resize(ss);
    /** Aspect ratio, `GLSL`; use `min` for contain, `max` for cover. */
    aspect(ss, max, true, ar);

    return onResized?.(demo, w, h);
  };

  demo.fps = (((demo.fps ?? true) !== true)? demo.fps
    : getFPS({ style: 'color: #fff; background-color: #0006;', max: 2e2 }));

  demo.start = async (state = demo) => {
    const { opticalFlow, resize, to: { timer, frame: frameMPM } } = state;

    opticalFlow.on && await ofStart();

    addEventListener('resize', () => resize());
    resize();

    const timestep = timer.step;
    const checkFrame = () => checkStatus(to, frameMPM);

    demo.queued = ((!isFinite(timestep))? frame(checkFrame)
        : (function loop() {
            return demo.queued = setTimeout(() =>
                demo.queued = frame(() => {
                  demo.queued = void demo.queued?.cancel?.();
                  checkFrame();
                  loop();
                }),
              timestep);
          })());

    return state;
  };

  return self.mpm = to;
}

export default demo;
